import { intl, ui } from '@beelday/common'
import { useSelector } from 'app-redux'
import { TrainingRoomPhase } from 'common/types'
import { useQuestions, VotingModal } from 'features/questions'
import { FC } from 'react'
import { use100vh } from 'react-div-100vh'
import { RoomContainer } from 'room/common/presentation/room-container'
import RoomHeader from 'room/common/presentation/room-header'
import { InPersonLayout } from 'room/common/presentation/video-layout/in-person-layout'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useAssertedWorkflowUser } from 'room/common/use-workflow-user'
import { MiscPanel } from 'room/misc'
import { TraineeMenu } from 'room/training-room/trainee-menu'
import { SceneVideoUser } from 'video-conference-media'
import { selectFocusedFeature } from './redux'
import { TraineeFocusedFeatureInPerson } from './trainee-focused-feature-in-person'

/** Nobody publishes anything in a stationary interaction scheme. */
const NO_STREAMS: SceneVideoUser[] = []

/**
 * The participant's room on their own laptop, following what the projector
 * shows and answering from here.
 *
 * Today's room minus `VideoGrid`, `TrainerStreamView` and
 * `RecordingIndicator` - there are no streams and a stationary interaction
 * scheme enables no `ParamToggler`. `RoomHeader` and `MiscPanel` stay, and the
 * messaging tab stays with them: chat is deliberately kept, because a
 * participant who wants to ask something quietly still needs somewhere to do
 * it.
 *
 * A separate file rather than a branch inside `room-trainee.tsx` on purpose:
 * that file is the highest-traffic screen in the product and must not regress
 * for the sake of a variant that shares only its chrome.
 */
export const RoomTraineeInPerson: FC = () => {
	const containerHeight = use100vh()
	const roomAddress = useAssertedJoinedRoomAddress()
	const user = useAssertedWorkflowUser()
	const trainingRoomPhase = useSelector(state => state.room.trainingRoomPhase)
	const focusedFeature = useSelector(selectFocusedFeature)
	const questions = useQuestions()

	return (
		<RoomContainer
			style={{
				height: `${containerHeight}px`,
			}}
		>
			<RoomHeader />
			{trainingRoomPhase === TrainingRoomPhase.INITIAL ? (
				<>
					<InPersonLayout>
						{focusedFeature ? (
							<TraineeFocusedFeatureInPerson streams={NO_STREAMS} />
						) : null}
					</InPersonLayout>
					{questions.active && !focusedFeature ? (
						<VotingModal
							roomAddress={roomAddress}
							me={user.id}
							showResults={false}
						/>
					) : null}
					<TraineeMenu />
				</>
			) : (
				// `SELECTING_GROUPS` and `REVIEWING_TASKS` belong to the real
				// `Grouping` feature, which a stationary scheme does not enable.
				<InPersonLayout>
					<ui.ErrorInline>
						<intl.Translate>in_person.room.unexpected_phase</intl.Translate>
					</ui.ErrorInline>
				</InPersonLayout>
			)}
			<MiscPanel />
			<ui.BeeldayLogoAbsoluteBottom />
		</RoomContainer>
	)
}
